import { Box, Card, CardContent, Divider, Stack, useMediaQuery, useTheme } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import type { SiteRevenuePerMarketProgram } from '@/features/site/types/revenue'
import {
  assignColorsToMarketPrograms,
  excludeFeeMarketPrograms,
  getChartDataUsingMapOfMarketProgramsGroupedByMonth,
  getCostsDistributionChartOptions,
  getRevenuesChartOptions,
  getRevenuesDistributionChartOptions,
} from '@/features/site/utils/revenueUtils/chartUtils'
import { getRevenueDataFromMarketProgramsByMonth } from '@/features/site/utils/revenueUtils/formatRevenueData'
import { getCurrencyFromRevenues } from '@/features/site/utils/revenueUtils/getCurrencyFromRevenues'

const COMMON_CHART_STYLES = { height: '400px', width: '100%' }

type SiteRevenueChartsProps = {
  revenues: SiteRevenuePerMarketProgram | null
}

export function SiteRevenueCharts({ revenues }: Readonly<SiteRevenueChartsProps>) {
  const { t } = useTranslation()
  const theme = useTheme()
  // The bar chart always takes the full card width; the two distribution charts share a row on
  // xl screens, which with the app's side navigation leaves each of them wider than
  // PIE_SIDE_LEGEND_MIN_WIDTH, so the donut keeps its legend beside it.
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'))

  if (!revenues) {
    return null
  }

  const revenueDataFromMarketProgramsByMonth = getRevenueDataFromMarketProgramsByMonth(revenues)

  // The revenues bar chart only shows the programs considered revenues; fee programs are
  // charted in the costs distribution chart instead.
  const { programs, seriesData } = getChartDataUsingMapOfMarketProgramsGroupedByMonth(
    excludeFeeMarketPrograms(revenueDataFromMarketProgramsByMonth),
    t,
  )
  const rawPrograms = Object.keys(revenueDataFromMarketProgramsByMonth)
  const colors = assignColorsToMarketPrograms(rawPrograms, t)
  const currency = getCurrencyFromRevenues(revenues)

  const barChartOptions = getRevenuesChartOptions(programs, seriesData, colors, currency)
  const pieChartOptions = getRevenuesDistributionChartOptions(revenueDataFromMarketProgramsByMonth, colors, t, currency)
  const costsChartOptions = getCostsDistributionChartOptions(revenueDataFromMarketProgramsByMonth, colors, t, currency)

  const shouldDisplayTheBarChart = programs.length > 0
  // The revenues distribution only makes sense when more than one revenue program has a
  // positive total; the costs distribution is shown whenever any fee program has a cost
  // to display, even a single one.
  const shouldDisplayThePieChart = pieChartOptions.series[0].data.length > 1
  const shouldDisplayTheCostsPieChart = costsChartOptions.series[0].data.length > 0

  // Nothing to chart (e.g. the selected period has no data): skip the empty card entirely
  if (!shouldDisplayTheBarChart && !shouldDisplayThePieChart && !shouldDisplayTheCostsPieChart) {
    return null
  }

  return (
    <Card>
      <CardContent>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {shouldDisplayTheBarChart && (
            <Box>
              <CustomTypography variant="h4">{t('customer_revenue.chart.revenues_title')}</CustomTypography>
              <ReactECharts data-testid="revenue-bar-chart" option={barChartOptions} style={COMMON_CHART_STYLES} />
            </Box>
          )}

          {/* minWidth: 0 lets each chart shrink when the row layout kicks in: an ECharts canvas has a
              fixed pixel width, which would otherwise hold its flex item at the previous width */}
          {(shouldDisplayThePieChart || shouldDisplayTheCostsPieChart) && (
            <Stack
              direction={isExtraLargeScreen ? 'row' : 'column'}
              divider={<Divider flexItem orientation={isExtraLargeScreen ? 'vertical' : 'horizontal'} />}
              spacing={2}
            >
              {shouldDisplayThePieChart && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTypography variant="h4">
                    {t('customer_revenue.chart.revenues_distribution_title')}
                  </CustomTypography>
                  <ReactECharts data-testid="revenue-pie-chart" option={pieChartOptions} style={COMMON_CHART_STYLES} />
                </Box>
              )}

              {shouldDisplayTheCostsPieChart && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTypography variant="h4">
                    {t('customer_revenue.chart.costs_distribution_title')}
                  </CustomTypography>
                  <ReactECharts data-testid="costs-pie-chart" option={costsChartOptions} style={COMMON_CHART_STYLES} />
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
